import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trash2, UserPlus, Users } from 'lucide-react';
import Avatar from '@/components/primitives/Avatar.jsx';
import Badge from '@/components/primitives/Badge.jsx';
import Button from '@/components/primitives/Button.jsx';
import Card from '@/components/primitives/Card.jsx';
import Select from '@/components/primitives/Select.jsx';
import EmptyState from '@/components/patterns/EmptyState.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import { getMembers, updateMember } from '@/redux/requests/membersRequests.js';
import { getRoles } from '@/redux/requests/rolePermissionsRequests.js';
import { rolePermissionMembers, rolePermissionRoles } from '@/redux/selectors/settingsSelectors.js';
import { activeCompany, authPermissions, authSessionUser } from '@/redux/selectors/userSelectors.js';
import CreateOrganizationDialog from './CreateOrganizationDialog.jsx';
import InviteMemberDialog from './InviteMemberDialog.jsx';
import RemoveMemberDialog from './RemoveMemberDialog.jsx';

/** Who is in the organization, with which role, and the way to invite, change roles or remove. */
const MembersSection = () => {
  const company = useSelector(activeCompany);
  const user = useSelector(authSessionUser);
  const permissions = useSelector(authPermissions);
  const members = useSelector(rolePermissionMembers);
  const roles = useSelector(rolePermissionRoles);
  const [isInviting, setIsInviting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const roleItems = useMemo(() => roles.map((role) => ({ value: role.id, label: role.name })), [roles]);
  const defaultRoleId = roles.find((role) => role.name === 'Member')?.id || roles[0]?.id;
  const canInvite = permissions.includes('member:create');
  const canEdit = permissions.includes('member:edit');
  const canRemove = permissions.includes('member:delete');

  useEffect(() => {
    if (company?.id && !company.isPersonal) {
      getMembers();
      getRoles();
    }
  }, [company?.id, company?.isPersonal]);

  if (company?.isPersonal) {
    return (
      <>
        <EmptyState
          icon={Users}
          title="Members belong to an organization"
          description="Your personal workspace is only yours. Create an organization to invite your team and share connections, knowledge and automations with them."
          action={
            <Button variant="primary" onClick={() => setIsCreating(true)}>
              Create organization
            </Button>
          }
        />
        <CreateOrganizationDialog open={isCreating} onOpenChange={setIsCreating} />
      </>
    );
  }

  const handleRoleChange = async (member, roleId) => {
    if (roleId === member.roleId) {
      return;
    }

    const res = await updateMember({ userId: member.userId, roleId });
    await getMembers();

    if (res?.success === false) {
      toastManager.add({ type: 'error', title: 'Role not changed', description: res.message });
      return;
    }

    toastManager.add({ type: 'success', title: `${member.isPending ? member.email : member.name || member.email} is now ${roles.find((role) => role.id === roleId)?.name}` });
  };

  return (
    <Card
      title="Members"
      description={`${members.length} ${members.length === 1 ? 'person' : 'people'} in ${company?.name}.`}
      actions={
        canInvite ? (
          <Button variant="primary" iconStart={UserPlus} onClick={() => setIsInviting(true)}>
            Invite
          </Button>
        ) : null
      }>
      <ul className="settings-members">
        {members.map((member) => {
          const isSelf = member.userId === user?.id;
          /* An invite has no name until the person accepts it, so it goes by its email. */
          const displayName = member.isPending ? member.email : member.name || member.email;
          return (
            <li key={member.userId || member.email} className="settings-member">
              <Avatar name={displayName} src={member.imageUrl} />
              <div className="settings-member__who">
                <span className="settings-member__name">
                  {displayName}
                  {isSelf && <span className="settings-member__you"> (you)</span>}
                </span>
                {!member.isPending && <span className="settings-member__email">{member.email}</span>}
              </div>
              {member.isPending && <Badge tone="warning">Invite sent</Badge>}
              <div className="settings-member__role">{canEdit && !isSelf ? <Select label={`Role for ${displayName}`} items={roleItems} value={member.roleId} onValueChange={(roleId) => handleRoleChange(member, roleId)} /> : <span className="settings-member__role-name">{member.roleName}</span>}</div>
              {canRemove && !isSelf ? <Button variant="ghost" size="sm" iconOnly iconStart={Trash2} label={`Remove ${displayName}`} onClick={() => setMemberToRemove(member)} /> : <span className="settings-member__spacer" />}
            </li>
          );
        })}
      </ul>
      <InviteMemberDialog open={isInviting} onOpenChange={setIsInviting} roleItems={roleItems} defaultRoleId={defaultRoleId} organizationName={company?.name} />
      <RemoveMemberDialog member={memberToRemove} organizationName={company?.name} onOpenChange={(open) => !open && setMemberToRemove(null)} />
    </Card>
  );
};

export default MembersSection;
